import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../constants";
import { useHistory } from "react-router-dom";

export default function EditUserForm({ id, rafraichir, token, admin }) {
  const [newUser, setNewUser] = useState({ mail: "" });
  const [messageMailInvalide, setMessageMailInvalide] = useState("");
  const [messageUpdateMail, setMessageUpdateMail] = useState("");

  const [countryCode, setCountryCode] = useState("+33");
  const [localPhone, setLocalPhone] = useState("");
  const [phoneStatus, setPhoneStatus] = useState("");
  const [phoneMessage, setPhoneMessage] = useState("");
  const phoneMsgTimeout = useRef();
  const [currentPhone, setCurrentPhone] = useState("");

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const navigate = useHistory();

  const COUNTRY_CODES = [
    { code: "+33", label: "🇫🇷 France" },
    { code: "+32", label: "🇧🇪 Belgique" },
    { code: "+41", label: "🇨🇭 Suisse" },
    { code: "+1", label: "🇺🇸 USA / Canada" },
    { code: "+34", label: "🇪🇸 Espagne" },
    { code: "+49", label: "🇩🇪 Allemagne" },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setMessageMailInvalide("");
      setMessageUpdateMail("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [messageMailInvalide, messageUpdateMail]);

  useEffect(() => {
    const fetchUserData = async () => {
      setCurrentPhone("");
      setLocalPhone("");
      try {
        const res = await axios.get(`${API_URL}/user`, {
          headers: { Authorization: "Bearer " + token },
        });

        const user = (res.data.users || res.data).find((u) => u._id === id);
        if (!user) return;

        setTwoFactorEnabled(user.twoFactorEnabled);
        setNewUser({ mail: user.email });

        if (user.phone?.startsWith("+")) {
          setCurrentPhone(user.phone);
          const match = COUNTRY_CODES.find((c) =>
            user.phone.startsWith(c.code)
          );
          if (match) {
            setCountryCode(match.code);
            setLocalPhone(user.phone.replace(match.code, ""));
          } else {
            setLocalPhone(user.phone.replace(/^\+/, ""));
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (id) fetchUserData();
  }, [id, token]);

  const validateE164 = (phone) => /^\+[1-9]\d{7,14}$/.test(phone);

  const buildPhoneNumber = () => {
    const cleaned = localPhone.replace(/\D/g, "");
    if (!cleaned) return "";
    return `${countryCode}${cleaned}`;
  };

  const handleVerifyPhone = () => {
    const fullPhone = buildPhoneNumber();
    if (!fullPhone) {
      setPhoneStatus("invalid");
      setPhoneMessage("Numéro vide");
      return;
    }

    if (validateE164(fullPhone)) {
      setPhoneStatus("valid");
      setPhoneMessage(`Numéro valide : ${fullPhone}`);
    } else {
      setPhoneStatus("invalid");
      setPhoneMessage("Numéro invalide");
    }
  };

  const toggle2FA = async (enable) => {
    try {
      const res = await axios.patch(
        `${API_URL}/user/update`,
        { userId: id, twoFactorEnabled: enable },
        { headers: { Authorization: "Bearer " + token } }
      );
      setMessageUpdateMail(res.data.message);
      rafraichir();
    } catch (error) {
      setMessageMailInvalide(error.response?.data?.error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.mail)) {
      setMessageMailInvalide("Adresse e-mail invalide");
      return;
    }

    const phone = buildPhoneNumber();
    if (localPhone && !validateE164(phone)) {
      setPhoneStatus("invalid");
      setPhoneMessage("Numéro invalide");
      return;
    }

    try {
      const res = await axios.patch(
        `${API_URL}/user/update`,
        { userId: id, email: newUser.mail, admin, phone },
        { headers: { Authorization: "Bearer " + token } }
      );
      setMessageUpdateMail(res.data.message);
      rafraichir();
    } catch (error) {
      setMessageMailInvalide(error.response?.data?.error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col items-center w-full max-w-lg mx-auto px-4 sm:px-6"
    >
      {/* 2FA */}
      <label className="flex items-center space-x-3 mt-4">
        <input
          type="checkbox"
          checked={twoFactorEnabled}
          onChange={(e) => {
            setTwoFactorEnabled(e.target.checked);
            toggle2FA(e.target.checked);
          }}
        />
        <span className="text-sm md:text-base">
          {twoFactorEnabled
            ? "Désactiver la double authentification"
            : "Activer la double authentification"}
        </span>
      </label>

      {/* Téléphone */}
      <label className="w-full mt-4">
        {currentPhone && (
          <div className="mb-2 text-base text-gray-700 font-semibold">
            Numéro actuel : <span className="text-blue-700">{currentPhone}</span>
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="w-full sm:w-40 border-2 border-bleuCielSTB rounded-3xl p-3 bg-white text-sm md:text-base"
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label} ({c.code})
              </option>
            ))}
          </select> 

          <input
            className="w-full flex-1 border-2 border-bleuCielSTB rounded-3xl p-3 text-sm md:text-base lg:text-xl"
            value={localPhone}
            onChange={(e) => {
              setLocalPhone(e.target.value); 
              setPhoneStatus("");
              setPhoneMessage("");
            }}
            placeholder="Numéro de téléphone"
            type="tel"
          />

          <button
            type="button"
            onClick={handleVerifyPhone}
            className="w-full sm:w-auto border border-bleuSTB text-bleuSTB px-4 py-2 rounded-full text-sm hover:bg-bleuSTB hover:text-white transition"
          >
            Vérifier
          </button>
        </div>

        {phoneStatus === "valid" && phoneMessage && (
          <p className="text-green-600 bg-green-100 rounded-lg px-4 py-2 mt-2 text-sm md:text-base">
            {phoneMessage}
          </p>
        )}
        {phoneStatus === "invalid" && phoneMessage && (
          <p className="text-red-600 bg-red-100 rounded-lg px-4 py-2 mt-2 text-sm md:text-base">
            {phoneMessage}
          </p>
        )}
        {phoneStatus === "saved" && phoneMessage && (
          <p className="text-green-600 bg-green-100 rounded-lg px-4 py-2 mt-2 text-sm md:text-base">
            {phoneMessage}
          </p>
        )}
      </label>
      <button
        type="button"
        disabled={phoneStatus !== "valid"}
        onClick={async () => {
          const phone = buildPhoneNumber();
          if (!validateE164(phone)) return;
          try {
            const res = await axios.patch(
              `${API_URL}/user/update`,
              { userId: id, phone },
              { headers: { Authorization: "Bearer " + token } }
            );
            setPhoneMessage("Numéro enregistré !");
            setPhoneStatus("saved");
            rafraichir();
            if (phoneMsgTimeout.current) clearTimeout(phoneMsgTimeout.current);
            phoneMsgTimeout.current = setTimeout(() => {
              setPhoneMessage("");
              setPhoneStatus("");
            }, 3000);
          } catch (error) {
            setPhoneMessage(error.response?.data?.error || "Erreur lors de l'enregistrement");
            setPhoneStatus("invalid");
          }
        }}
        className={`bg-bleuSTB hover:bg-marronSTB text-white rounded-full py-2 px-6 mt-4 text-sm md:text-base lg:text-lg cursor-pointer transition ${phoneStatus === "valid" ? "hover:bg-blue-800" : "opacity-50 cursor-not-allowed"}`}
      >
        Ajouter / Mettre à jour
      </button>

      {/* Email */}
      <input
        className="w-full border-2 border-bleuCielSTB rounded-3xl p-3 mt-4 text-center text-sm md:text-base lg:text-xl"
        value={newUser.mail}
        onChange={(e) =>
          setNewUser({ ...newUser, mail: e.target.value })
        }
        placeholder="Nouvelle adresse mail"
        type="email"
      />

      <input
        type="submit"
        value="Changer l'adresse mail"
        className="bg-bleuSTB hover:bg-marronSTB text-white rounded-full py-2 px-6 mt-4 text-sm md:text-base lg:text-lg cursor-pointer transition"
      />

      {messageMailInvalide && (
        <p className="bg-red-500 text-white rounded-lg px-4 py-2 mt-4 text-sm md:text-base">
          {messageMailInvalide}
        </p>
      )}
      {messageUpdateMail && (
        <p className="bg-green-500 text-white rounded-lg px-4 py-2 mt-4 text-sm md:text-base">
          {messageUpdateMail}
        </p>
      )}
    </form>
  );
}
